import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import ButtonDropdown from "@cloudscape-design/components/button-dropdown"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { forwardRef } from "react"
import { useNavigate } from "react-router-dom"
import type { DTADetails } from "../../../services/dtaApi"
import type { DTAVersion } from "../../../types/dta"
import { getDTADisplayStatus, stageBadgeColor } from "../../../components/shared/dtaDisplay"
import AnimatedDots from "./AnimatedDots"

interface DataStreamHeaderProps {
  /**
   * The loaded DTA details object.
   */
  stream: DTADetails
  /**
   * The DTA identifier.
   */
  dtaId: string
  /**
   * Available versions for this DTA.
   */
  versions: DTAVersion[]
  /**
   * Currently selected version string.
   */
  selectedVersion: string
  /**
   * The workflow stage currently being displayed.
   */
  displayStage: string | undefined
  /**
   * Whether a version switch is loading.
   */
  versionLoading: boolean
  /**
   * Whether the caller has the internal (sponsor) experience.
   */
  isDAE: boolean | undefined
  /**
   * Callback when the version selection changes.
   */
  onVersionChange: (version: string) => void
  /**
   * Lifecycle action buttons (Amend DTA, workflow CTA), rendered right-aligned on the same row as
   * the title/version/stage metadata.
   */
  actions?: React.ReactNode
  /**
   * Contextual guidance for the current stage and the caller's experience, rendered as muted inline
   * helper text under the title metadata (not a banner). Null when the stage needs no guidance. See
   * stageGuidanceText.
   */
  guidance?: string | null
  /**
   * Async source-document extraction status. When IN_PROGRESS, an animated "Extracting" badge
   * renders next to the stage badge.
   */
  extractionStatus?: DTADetails["extraction_status"]
  /**
   * Tab bar (and its content) rendered inside the header card, directly beneath the title row, so
   * the tab-row content actions share the same centered box (and right edge) as the lifecycle
   * actions above.
   */
  tabs?: React.ReactNode
}

/**
 * Renders the data stream header: title, metadata, contextual stage guidance, version
 * selector/compare, and the lifecycle action buttons (right-aligned). Transient page-level messages
 * (blocking validation, recovered draft) render near the top of the page/editor; errors and status
 * surface as toasts and in-content states. Content actions live in the tab row (see
 * DataStreamContentActions).
 */
const DataStreamHeader = forwardRef<HTMLElement, DataStreamHeaderProps>(function DataStreamHeader(
  {
    stream,
    dtaId,
    versions,
    selectedVersion,
    displayStage,
    versionLoading,
    isDAE,
    onVersionChange,
    actions,
    guidance,
    extractionStatus,
    tabs,
  },
  ref,
) {
  const navigate = useNavigate()

  const handleVersionSelect = (ver: string) => {
    onVersionChange(ver)
    window.history.replaceState(null, "", `/dtas/${dtaId}/v/${ver}`)
  }

  return (
    <header className="dta-header" ref={ref}>
      <div className="dta-header-main">
        <div className="dta-header-left">
          <SpaceBetween size="xxs" direction="vertical">
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Header variant="h1">{stream.domains?.[0] || "Untitled"}</Header>
              {/* External callers can't open /studies/:id (an internal-experience route), so hide the link. */}
              {isDAE && (
                <Button
                  variant="inline-link"
                  iconName="external"
                  onClick={() => navigate(`/studies/${stream.study_id}`)}
                >
                  Go to Study
                </Button>
              )}
            </SpaceBetween>
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Box color="text-status-inactive" fontSize="body-s">
                {stream.source_type === "edc"
                  ? "EDC"
                  : stream.source_type === "sdtm"
                    ? "SDTM"
                    : `Third-party (${stream.vendor_name || stream.vendor_id})`}
              </Box>
              <Box color="text-status-inactive" fontSize="body-s">
                &bull;
              </Box>
              <span data-testid="version-selector">
                <ButtonDropdown
                  variant="normal"
                  ariaLabel={`Version v${selectedVersion || versions[0]?.version || stream.version}`}
                  items={[
                    {
                      id: "versions-group",
                      text: "Switch version",
                      items:
                        versions.length > 0
                          ? versions.map((v) => ({
                              id: `v:${v.version}`,
                              text: `v${v.version} (${v.workflow_stage || "ACTIVE"})`,
                            }))
                          : [
                              {
                                id: `v:${stream.version}`,
                                text: `v${stream.version} (${getDTADisplayStatus(stream)})`,
                              },
                            ],
                    },
                    ...(versions.length >= 2
                      ? [{ id: "compare", text: "Compare versions", iconName: "expand" as const }]
                      : []),
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "compare") navigate(`/dtas/${dtaId}/compare`)
                    else if (detail.id.startsWith("v:")) handleVersionSelect(detail.id.slice(2))
                  }}
                >
                  v{selectedVersion || versions[0]?.version || stream.version}
                </ButtonDropdown>
              </span>
              {displayStage && (
                <>
                  <Box color="text-status-inactive" fontSize="body-s">
                    &bull;
                  </Box>
                  <Badge color={stageBadgeColor(displayStage)}>{displayStage}</Badge>
                  {stream.latest_version && selectedVersion !== stream.latest_version && (
                    <Badge color="grey">Read-only</Badge>
                  )}
                </>
              )}
              {extractionStatus === "IN_PROGRESS" && (
                <Badge color="blue">
                  Extracting
                  <AnimatedDots />
                </Badge>
              )}
            </SpaceBetween>
            {guidance && (
              <Box color="text-body-secondary" fontSize="body-m">
                {guidance}
              </Box>
            )}
          </SpaceBetween>
        </div>
        <div className="dta-header-right">
          {versionLoading ? (
            <Box color="text-status-inactive" fontSize="body-s">
              Loading...
            </Box>
          ) : null}
          {stream.locked && <Icon name="lock-private" size="medium" />}
          {actions}
        </div>
      </div>
      {tabs && <div className="dta-header-tabs">{tabs}</div>}
    </header>
  )
})

export default DataStreamHeader
